import { useState, useMemo, useEffect } from 'react'
import { useWireState } from '@forminator/react-wire'
import { useLabelStudio } from '@hook/useLabelStudio'
import { GCS_BUCKET_DOCUMENTS, GCS_BUCKET_INDEX_DOCUMENTS, RECORD_TYPE_DOCUMENT } from '@constants'
import * as store from '$store'
import CircleSpinner from '@components/shared/CircleSpinner'
import LabelStudioEditor from '@components/shared/LabelStudioEditor'
import { buildRecordUrl } from '@utils'
import Pagination from '@components/shared/Pagination'
import { useFetchDocument } from '@components/collection/DocumentView/DocumentView.hooks'
import { useCallbackRef } from '@hook/useCallbackRef'

const KSLabelStudio = () => {
    
    const [refEditor, editorNode, clearElement] = useCallbackRef()
    
    const [enabled, setEnabled] = useState(false)
    const [record, setRecord] = useState(null)
    const [pageIndex, setPageIndex] = useState(0)
    
    const [documentId, setDocumentId] = useWireState(store.ksLsDocId)
    
    const pageCount = useMemo(() => record?.pages?.length ?? 0, [record])
    
    const documentPageUrls = useMemo(() => (
        record?.pages?.map(it => buildRecordUrl(it, false, record.collection?.isParse))
    ), [record])
    
    const imageUrl = useMemo(() => (
        documentPageUrls?.[pageIndex ?? 0] ?? null
    ), [documentPageUrls, pageIndex])
    
    const ready = useMemo(() => (
        enabled &&
        record?.id?.length > 0 &&
        Boolean(editorNode)
    ), [
        enabled,
        record,
        editorNode,
    ])
    
    const labelStudioConfig = useMemo(() => !ready ? {
        enabled: false,
        recordType: RECORD_TYPE_DOCUMENT,
    } : {
        enabled: true,
        url: imageUrl,
        gcsBucket: record?.collection?.isParse ? GCS_BUCKET_INDEX_DOCUMENTS : GCS_BUCKET_DOCUMENTS,
        editorNode,
        record,
        setRecord,
        recordType: RECORD_TYPE_DOCUMENT,
        pageIndex,
        showAllLabels: true,
        showPrivateLabels: true,
        useOrganizationLabels: false,
        useCollectionLabels: true,
    }, [
        ready,
        record,
        pageIndex,
        editorNode,
    ])
    
    const isFetchingDocument = useFetchDocument(
        documentId,
        data => {
            console.log('fetch doc ok', data)
            setRecord(data)
        },
        e => console.error('KSLabelStudio', e),
    )
    
    const { labelStudioError, labelStudioSaving } = useLabelStudio(labelStudioConfig)
    
    useEffect(() => {
        
        if (!enabled) {
            console.warn('cleared editor node')
            clearElement()
        }
        
    }, [enabled, clearElement])
    
    return (
        
        <div id="KSLabelStudio" className="">
            
            <header className="flex items-center gap-4">
                
                <button
                    className="btn"
                    onClick={() => setEnabled(!enabled)}>
                    {enabled ? 'DISABLE' : 'ENABLE'} LS
                </button>
                
                <input
                    className="input input-bordered w-full"
                    type="text"
                    value={documentId}
                    onChange={e => setDocumentId(e.target.value)} />
                
                <Pagination
                    count={pageCount}
                    itemsPerPage={1}
                    pageRangeDisplayed={3}
                    forcePage={pageIndex}
                    setPageIndex={setPageIndex}
                    onPageChange={e => setPageIndex(e.selected)} />
            
            </header>
            
            {/* <div>
                <pre>
                    <code>{JSON.stringify({
                        enabled,
                        ready,
                        labelStudioError,
                        labelStudioSaving,
                        hasAssignedLabels,
                        isFetchingDocument,
                        record: record?.name ?? 'Unknown',
                        editorNode: editorNode ? 'ok' : 'not ready',
                    }, null, 4)}</code>
                </pre>
            </div> */}
            
            {labelStudioError && (
                <div className="absolute-centered">
                    <p>There was a problem loading the annotations editor.</p>
                    <p><code>{JSON.stringify(labelStudioError)}</code></p>
                </div>
            )}
            
            {!ready && isFetchingDocument && (
                <div className="absolute-centered">
                    <p>Fetching record...</p>
                </div>
            )}
            
            {!ready && !isFetchingDocument && (
                <div className="absolute-centered">
                    {record
                        ? <p>Enable LS when ready to begin</p>
                        : <p>Enter a document ID to begin</p>}
                </div>
            )}
            
            {ready && !record && (
                <div className="absolute-centered flex flex-col justify-center items-center">
                    <CircleSpinner />
                    <span>Loading record...</span>
                </div>
            )}
            
            {enabled && record && (
                <LabelStudioEditor
                    ref={refEditor}
                    className="border-4 border-red-500"
                    error={labelStudioError}
                    isSaving={labelStudioSaving} />
            )}
        
        </div>
        
    )
    
}

export default KSLabelStudio
